import NumberFlow from '@number-flow/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

import { WarningIcon } from '@treams/treams-icons';
import { DataCard, IconColor, Tooltip, tokens } from '@treams/treams-ui';

import { ScoreCategory, ScoreModuleState } from '../TreamsScore.types';
import type { ScoreColors, ScoreFactor } from '../TreamsScore.types';
import { toDisplayScore } from '../scoreDisplay';

interface IProps {
	factors: ScoreFactor[];
	getScoreColors: (score: number) => ScoreColors;
	engagementDaysRemaining: number | null;
}

const StyledRow = styled(Flex)`
	width: 100%;
	align-items: center;
	padding: 12px 0;
	border-top: 1px solid ${tokens.border.subtle};
`;

const StyledColumns = styled(Flex)`
	width: 100%;
	flex-direction: column;

	@media screen and (min-width: 70em) {
		flex-direction: row;
		column-gap: 48px;
	}
`;

const StyledColumn = styled(Box)`
	width: 100%;

	@media screen and (min-width: 70em) {
		flex: 1 1 0;
		min-width: 0;
	}
`;

// Figma fills column-first: the first half of the factors goes in the left
// column, the rest in the right one.
const ScoreBreakDown = ({ factors, getScoreColors, engagementDaysRemaining }: IProps) => {
	const splitIndex = Math.ceil(factors.length / 2);
	const columns = [factors.slice(0, splitIndex), factors.slice(splitIndex)];

	return (
		<StyledColumns mt={2}>
			{columns.map((column, index) => (
				<StyledColumn key={index}>
					{column.map((factor) => (
						<ScoreBreakdownRow
							key={factor.category}
							factor={factor}
							getScoreColors={getScoreColors}
							engagementDaysRemaining={engagementDaysRemaining}
						/>
					))}
				</StyledColumn>
			))}
		</StyledColumns>
	);
};

const ScoreBreakdownRow = ({
	factor,
	getScoreColors,
	engagementDaysRemaining,
}: {
	factor: ScoreFactor;
	getScoreColors: (score: number) => ScoreColors;
	engagementDaysRemaining: number | null;
}) => {
	const theme = useTheme();
	const { t } = useTranslation();

	const { isUnavailable, isDisabled, isInactive, displayScore, background } = useMemo(() => {
		const unavailable = factor.state === ScoreModuleState.Unavailable;
		const disabled = factor.state === ScoreModuleState.Disabled;
		const score = toDisplayScore(disabled ? 0 : factor.score);

		return {
			isUnavailable: unavailable,
			isDisabled: disabled,
			isInactive: unavailable || disabled,
			displayScore: score,
			background: getScoreColors(score).background,
		};
	}, [factor.state, factor.score, getScoreColors]);

	const showReliabilityWarning =
		factor.category === ScoreCategory.Engagement && !isInactive && engagementDaysRemaining !== null;

	return (
		<StyledRow>
			<DataCard background={isUnavailable ? '#F1F3F5' : background}>
				{isUnavailable ? (
					<Box style={{ width: 12, height: 2, borderRadius: 1, background: '#C2CAD2' }} />
				) : (
					<NumberFlow value={displayScore} format={{ maximumFractionDigits: 1 }} locales="en-US" />
				)}
			</DataCard>
			<Box
				style={{
					marginLeft: 12,
					color: isInactive ? '#C2CAD2' : tokens.text.primary,
					fontSize: 15,
				}}
			>
				{factor.label}
			</Box>
			{showReliabilityWarning && (
				<Box
					style={{
						marginLeft: 8,
						display: 'inline-flex',
						alignItems: 'center',
						transform: 'translateY(2px)',
					}}
				>
					<Tooltip
						content={t('analytics.treamsScore.factor.engagementUnreliableTooltip', {
							days: engagementDaysRemaining,
						})}
						position="top"
					>
						<span
							aria-label={t('analytics.treamsScore.factor.engagementUnreliableAriaLabel')}
							style={{ cursor: 'default' }}
						>
							<IconColor color="#F7B500">
								<WarningIcon style={{ width: '18px', height: '18px' }} />
							</IconColor>
						</span>
					</Tooltip>
				</Box>
			)}
			{isDisabled && (
				<Box style={{ marginLeft: 12, color: tokens.text.secondary, fontSize: 15 }}>
					{t('analytics.treamsScore.factor.notInUse')}
				</Box>
			)}
			{isUnavailable && (
				<Box style={{ marginLeft: 12, display: 'inline-flex' }}>
					<Tooltip content={t('analytics.treamsScore.factor.upgradeTooltip')} position="top">
						<a
							href={`mailto:support@treams.nl?subject=${encodeURIComponent(
								t('analytics.treamsScore.factor.upgradeMailSubject'),
							)}`}
							aria-label={t('analytics.treamsScore.factor.upgradeTooltip')}
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								textDecoration: 'none',
								cursor: 'pointer',
								background: tokens.text.primary,
								color: theme.colors.white,
								fontSize: 13,
								fontWeight: 600,
								borderRadius: 8,
								padding: '4px 10px',
							}}
						>
							{t('analytics.treamsScore.factor.proBadge')}
						</a>
					</Tooltip>
				</Box>
			)}
			<Box
				style={{
					marginLeft: 'auto',
					color: tokens.text.secondary,
					fontSize: 15,
				}}
			>
				<NumberFlow value={factor.weight} suffix="%" locales="en-US" />
			</Box>
		</StyledRow>
	);
};

export default ScoreBreakDown;
